// libs
import { ContentItemQuery, useDeleteContentItem } from '@lopo-tech/lopo-api'
import React, { useCallback, useMemo, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'

// interfaces / constants
import { CLASS_PREFIX } from 'src/constants/'
import { FEED_POST_TYPE_EVENT } from 'src/constants/feed'
import { IMoreOption, MORE_OPTIONS } from 'src/constants/more_button'
import { UPDATE_POST } from 'src/constants/urls'
import { COLOR_TYPE_SECONDARY6 } from 'src/utils/color'
import { IHeaderDetailsTracker } from 'src/utils/reporting/events_tracking/events_tracking'

// classes
import CreateFlagWizard from 'src/components/flag/create/create_flag_wizard'
import MoreMenu from 'src/components/header/subheaders/shared_contents/more_menu/more_menu'
import MenuItemSegment from 'src/components/item_segment/layouts/menu/menu'
import LoadingSpinner from 'src/components/loading_spinner/loading_spinner'
import {
  MSGBOX_BUTTON_TYPE_CANCEL,
  MSGBOX_BUTTON_TYPE_DANGER
} from 'src/components/message_box/message_box'
import MessageBoxWithModal from 'src/components/message_box/message_box_with_modal'
import Modal from 'src/components/modal/modal'
import { useShare } from 'src/components/share/lib/use_share'
import { ShareTargetList } from 'src/components/share/share_targets/share_targets'
import PostCreate from 'src/containers/post/create'
import PostUpdate from 'src/containers/post/update'

// helpers
import { textResources } from 'src/lang/de'
import { UrlUtils } from 'src/utils/url/url'

import './more_button.scss'

const cls = CLASS_PREFIX + 'more-button'
const labels = textResources.item

interface IProps {
  contentItem: ContentItemQuery['contentItem'];
  postUpdateOpen?: boolean;
  getNewsfeedRoute: () => string;
  tracking?: IHeaderDetailsTracker;
}

export const MoreButton: React.FC<IProps> = ({
  contentItem,
  tracking,
  postUpdateOpen,
  getNewsfeedRoute
}) => {
  const history = useHistory()
  const match = useRouteMatch()

  const [deleteContentItem] = useDeleteContentItem()

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [flagModalOpen, setFlagModalOpen] = useState(false)
  const [renderPostUpdate, setRenderPostUpdate] = useState(!!postUpdateOpen)
  const [renderPostDuplicate, setRenderPostDuplicate] = useState(false)
  const [showShareList, setShowShareList] = useState(false)

  const { canDestroy, canFlag, canUpdate, canShare } = contentItem ?? {}
  const { canMessage } = contentItem?.author || {}

  const options: IMoreOption[] = useMemo(() => {
    const opts: IMoreOption[] = []

    if (canShare?.value) {
      opts.push(MORE_OPTIONS.SHARE)
    }
    if (canUpdate?.value) {
      opts.push(MORE_OPTIONS.UPDATE)
    }
    if (canMessage?.value) {
      opts.push(MORE_OPTIONS.MESSAGE)
    }
    if (canDestroy?.value) {
      opts.push(MORE_OPTIONS.DELETE)
    }
    if (canFlag?.value) {
      opts.push(MORE_OPTIONS.FLAG)
    }
    // check permission. Only user who created post can duplicate it.
    // canCreateEvent && canUpdate?.value && isEvent && options.push(MORE_OPTIONS.DUPLICATE_EVENT)

    return opts
  }, [canShare, canUpdate, canMessage, canDestroy, canFlag])

  const onUpdate = useCallback(() => {
    if (!contentItem) {
      return
    }

    setRenderPostUpdate(true)
    history.push(
      UrlUtils.detailPage(contentItem.slug, contentItem.__typename) +
        UPDATE_POST,
      { startPath: match.url, search: history.location.search }
    )
  }, [contentItem, history, match.url])

  const duplicateEvent = useCallback(() => {
    setRenderPostDuplicate(true)

    history.push(
      UrlUtils.getPostCreateURL(
        match.url,
        history.location.search,
        false,
        FEED_POST_TYPE_EVENT
      )
    )
  }, [history, match.url])

  const closeMessageBox = useCallback(() => {
    setDeleteModalOpen(false)
  }, [])

  const clickDelete = useCallback(async () => {
    if (contentItem?.id) {
      await deleteContentItem({
        variables: { input: { id: contentItem.id } }
      })
    }

    closeMessageBox()
    history.push(getNewsfeedRoute())
  }, [
    contentItem,
    deleteContentItem,
    getNewsfeedRoute,
    history,
    closeMessageBox
  ])

  const hideFlagModal = useCallback(() => {
    setFlagModalOpen(false)
  }, [history, match.url])

  const hidePostUpdate = useCallback(() => {
    setRenderPostUpdate(false)
  }, [])

  const hidePostDuplicate = useCallback(() => {
    setRenderPostDuplicate(false)
  }, [])

  const showFlagModal = useCallback(() => {
    setFlagModalOpen(true)
  }, [])

  const showDeleteModal = useCallback(() => {
    setDeleteModalOpen(true)
  }, [])

  const onMessage = useCallback(() => {
    if (contentItem?.author) {
      const { id, __typename } = contentItem.author
      history.push(UrlUtils.sendMessageConversationPage(__typename, id))
    }
  }, [contentItem, history])

  const { copyLink, openShareSheet, targets, track } = useShare({
    title: contentItem?.title,
    tracking,
    url: contentItem ? UrlUtils.detailPage(contentItem.slug, contentItem.__typename) : '',
    utmSource: 'post'
  })

  const onShare = useCallback(() => {
    openShareSheet(() => setShowShareList(true))
  }, [openShareSheet])

  const closeShareList = useCallback(() => {
    setShowShareList(false)
  }, [])

  const handleMoreActionClick = (selected: IMoreOption) => {
    switch (selected.value) {
      case MORE_OPTIONS.DELETE.value:
        return showDeleteModal
      case MORE_OPTIONS.FLAG.value:
        return showFlagModal
      case MORE_OPTIONS.UPDATE.value:
        return onUpdate
      case MORE_OPTIONS.MESSAGE.value:
        return onMessage
      case MORE_OPTIONS.SHARE.value:
        return onShare
      case MORE_OPTIONS.DUPLICATE_EVENT.value:
        return duplicateEvent
      default:
        return undefined
    }
  }

  return (
    <div className={cls}>
      <MoreMenu onHide={closeShareList}>
        {showShareList && contentItem && (
          <ShareTargetList targets={targets} onTarget={track} onCopyLink={copyLink} />
        )}
        {options.map((option, index) => (
          <MenuItemSegment
            key={index}
            label={option.text}
            icon={{ name: option.icon }}
            navigation={{ onClick: handleMoreActionClick(option) }}
            hidden={showShareList}
            disabled={option.disabled}
            tooltip={option.tooltip}
          />
        ))}
      </MoreMenu>
      {flagModalOpen && (
        <Modal onClose={hideFlagModal}>
          <CreateFlagWizard
            flaggableId={contentItem?.id}
            onEnd={hideFlagModal}
          />
        </Modal>
      )}
      {renderPostUpdate
        ? (
          <React.Suspense fallback={<LoadingSpinner shown />}>
            <PostUpdate contentItem={contentItem} onClose={hidePostUpdate} />
          </React.Suspense>
        )
        : null}
      {renderPostDuplicate
        ? (
          <React.Suspense fallback={<LoadingSpinner shown />}>
            <PostCreate
              open
              postType={FEED_POST_TYPE_EVENT}
              defaultIconType={COLOR_TYPE_SECONDARY6}
              featureBlocked={false}
              onClose={hidePostDuplicate}
            />
          </React.Suspense>
        )
        : null}
      {deleteModalOpen && (
        <MessageBoxWithModal
          buttons={[
            {
              onClick: clickDelete,
              type: MSGBOX_BUTTON_TYPE_DANGER
            },
            {
              onClick: closeMessageBox,
              type: MSGBOX_BUTTON_TYPE_CANCEL
            }
          ]}
          onClose={closeMessageBox}
          title={labels.deletePost}
        >
          <p>{labels.deletePostConfirmationQuestion}</p>
        </MessageBoxWithModal>
      )}
    </div>
  )
}
